import type { ApiBid } from "../mappers/types";

/**
 * Reconstructs the nested group hierarchy required by the backend
 * `BidCreateAtomic` schema from the flat `ApiBid` arrays.
 *
 * The local state stores groups/items as flat arrays (same as `BidSchema`),
 * but the atomic endpoint expects nested `line_items_grouped` with
 * `children` and `line_items` embedded in each group.
 */
export function buildAtomicPayload(bid: ApiBid): Record<string, any> {
    const allGroups = bid.line_groups ?? [];
    const allItems: any[] = (bid as any).line_items ?? [];

    // Build root and sub-group shells
    const rootGroups = allGroups
        .filter((g) => g.parent_id === null)
        .map((g: any) => ({ ...g, children: [], line_items: [], addons: [] }));

    const subGroups = allGroups
        .filter((g) => g.parent_id !== null)
        .map((g: any) => ({ ...g, children: [], line_items: [], addons: [] }));

    // Nest sub-groups under their parent
    const rootMap = new Map(rootGroups.map((g) => [g.id, g]));
    subGroups.forEach((sub) => {
        const parent = rootMap.get(sub.parent_id);
        if (parent) parent.children.push(sub);
    });

    // Distribute line items into their parent group
    const subMap = new Map(subGroups.map((g) => [g.id, g]));
    allItems.forEach((item: any) => {
        const parent =
            rootMap.get(item.line_group_id) ?? subMap.get(item.line_group_id);
        if (parent) parent.line_items.push(item);
    });

    // Distribute addons into their parent group
    const bidLevelAddons: any[] = [];
    (bid.addons ?? []).forEach((addon: any) => {
        if (addon.line_group_id) {
            const parent =
                rootMap.get(addon.line_group_id) ??
                subMap.get(addon.line_group_id);
            if (parent) {
                parent.addons.push(addon);
                return;
            }
        }
        bidLevelAddons.push(addon);
    });

    // Strip client-only fields (virtual IDs, etc.) before sending
    const cleanGroup = (g: any): any => ({
        name: g.name,
        sort_order: g.sort_order ?? 0,
        parent_id: g.parent_id ?? null,
        rate_type: g.rate_type ?? "Days",
        is_internal: g.is_internal ?? false,
        markup: g.markup ?? 0,
        markup_amount: g.markup_amount ?? 0,
        budget_buffer: g.budget_buffer ?? 0,
        budget_buffer_amount: g.budget_buffer_amount ?? 0,
        external_notes: g.external_notes ?? "",
        children: (g.children ?? []).map(cleanGroup),
        line_items: (g.line_items ?? []).map(cleanItem),
        addons: (g.addons ?? []).map(cleanAddon),
    });

    const cleanItem = (item: any): any => ({
        name: item.name,
        vendor_rate: item.vendor_rate ?? 0,
        markup: item.markup ?? 0,
        markup_amount: item.markup_amount ?? 0,
        rate_type: item.rate_type ?? "Days",
        is_internal: item.is_internal ?? false,
        quantity: item.quantity ?? 0,
        days: item.days ?? 1,
        vendor_currency: item.vendor_currency ?? "USD",
        adjustment: item.adjustment ?? 0,
        ot_1_5: item.ot_1_5 ?? 0,
        ot_2_0: item.ot_2_0 ?? 0,
        exchange_rate_buffer: item.exchange_rate_buffer ?? 0,
        budget_buffer: item.budget_buffer ?? 0,
        budget_buffer_amount: item.budget_buffer_amount ?? 0,
        sort_order: item.sort_order ?? 0,
        internal_notes: item.internal_notes ?? "",
        external_notes: item.external_notes ?? "",
        service_item_id: item.service_item_id ?? null,
    });

    const cleanAddon = (addon: any): any => ({
        name: addon.name,
        amount: addon.amount ?? 0,
        type: addon.type ?? "flat",
    });

    return {
        name: bid.name || "Untitled Bid",
        status: "Draft",
        bid_type: (bid as any).bid_type || "Standard",
        project_id: (bid as any).project_id ?? null,
        visible_columns: bid.visible_columns ?? [],
        sticky_columns: bid.sticky_columns ?? [],
        assumptions: bid.assumptions ?? null,
        inclusions: bid.inclusions ?? null,
        exclusions: bid.exclusions ?? null,
        line_items_grouped: rootGroups.map(cleanGroup),
        addons: bidLevelAddons.map(cleanAddon),
    };
}
